import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import * as R from 'ramda'
import { Defaults, Text, useInterval } from '@pbt/pbt-ui-components'

import type { Estimate as GraphqlEstimate } from '~/api/graphql/generated/types'
import { PuiPagination } from '~/components/common/lists/table/PuiPagination'
import { PaymentBillingActivityTable } from '~/components/dashboard/clients/balance/table/payments/PaymentBillingActivityTable'
import {
  fetchPaymentPage,
  getClientBillingActivityPaymentError,
  getIsPollingPayments,
  getPaymentLoading,
  getPaymentPageData,
  getPaymentTotalCount,
  getShouldRefreshData,
} from '~/store/duck/clientBillingActivityData'
import { getPaymentsIsSaving } from '~/store/reducers/payments'
import { usePersistClickedItemOnURL } from '~/utils/routes'

import { useWidgetPageManagementHooks } from '../../../hooks/widgetPageManagementHooks'
import { CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS } from '../../table/common/tableConstants'

interface PaymentsWidgetProps {
  initialPage?: number
  isAddClientPaymentDialogOpened: boolean
  isExpanded: boolean
  onClickEstimate: (data: GraphqlEstimate) => void
  onExpand: () => void
}

export const PaymentsWidget = ({
  initialPage = 1,
  isAddClientPaymentDialogOpened,
  isExpanded,
  onClickEstimate,
  onExpand,
}: PaymentsWidgetProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('Common')
  const { clientId } = useParams()

  const refresh = useSelector(getShouldRefreshData)
  const paymentPageData = useSelector(getPaymentPageData)
  const paymentTotalCount = useSelector(getPaymentTotalCount)
  const isFetchingPayment = useSelector(getPaymentLoading)
  const isSaving = useSelector(getPaymentsIsSaving)
  const isPolling = useSelector(getIsPollingPayments)
  const error = useSelector(getClientBillingActivityPaymentError)
  const persistClickedItem = usePersistClickedItemOnURL('paymentWidgetPage')

  const { offsetValue, setURLState } = useWidgetPageManagementHooks({
    initialPage,
    itemsPerPage: CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS,
    persistClickedItem,
  })

  const isLoading = isPolling ? false : isFetchingPayment || isSaving
  const shouldFetchInitialData = Boolean(
    clientId && !isAddClientPaymentDialogOpened,
  )

  useEffect(() => {
    if (shouldFetchInitialData) {
      dispatch(
        fetchPaymentPage({
          clientId: clientId!,
          offset: offsetValue,
          limit: CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS,
        }),
      )
    }
  }, [shouldFetchInitialData])

  useEffect(() => {
    if (clientId && refresh) {
      dispatch(
        fetchPaymentPage({
          clientId,
          offset: offsetValue,
          limit: CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS,
          isRefreshing: true,
        }),
      )
    }
  }, [clientId, refresh])

  const handleFetchMore = (offset: number) => {
    if (clientId) {
      setURLState(offset)
      dispatch(
        fetchPaymentPage({
          clientId,
          offset,
          limit: CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS,
        }),
      )
    }
  }

  useInterval(() => {
    if (clientId) {
      dispatch(
        fetchPaymentPage({
          clientId: clientId!,
          offset: offsetValue,
          limit: CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS,
          isPolling: true,
        }),
      )
    }
  }, Defaults.BALANCE_PAGE_UPDATE_INTERVAL)

  if (error) {
    return (
      <Text px={2} py={1} variant="body2">
        {error}
      </Text>
    )
  }

  if (!isLoading && R.isEmpty(paymentPageData)) {
    return (
      <Text px={2} py={1} variant="body2">
        {t('Common:NO_PAYMENTS')}
      </Text>
    )
  }

  return (
    <>
      <PaymentBillingActivityTable
        isLoading={isLoading}
        payments={paymentPageData}
        paymentsPerPage={CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS}
        onEstimateNameClick={onClickEstimate}
        onExpand={onExpand}
      />
      {!isExpanded && (
        <Box display="flex" justifyContent="flex-end" p={1}>
          <PuiPagination
            initialPage={initialPage}
            itemsPerPage={CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS}
            reset={shouldFetchInitialData}
            totalItems={paymentTotalCount}
            onFetchMore={handleFetchMore}
          />
        </Box>
      )}
    </>
  )
}
